import { Suspense, lazy, useState } from "react";
import { Button, Modal, Spin, Tag } from "antd";
import {
  AppstoreOutlined,
  CalendarOutlined,
  DollarOutlined,
  EditOutlined,
  GiftOutlined,
  LinkOutlined,
  MailOutlined,
  RobotOutlined,
  SolutionOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import templatesJson from "../../automationList/templates.json";
import AiFlowPromptBox from "../../../comps/aiFlowPrompt/main";

// Lazy on purpose: the real React Flow preview pulls in the editor's node
// registry, which must never join this page's synchronous import graph
// (circular-import crash: "reading 'triggerComp'").
const TemplatePreviewFlow = lazy(() => import("./TemplatePreviewFlow"));

// Local type meta — deliberately NOT imported from nodes/cards: that module
// pulls the whole legacy editor chain into the list page and its circular
// imports leave NODE_META undefined at eval time ("reading 'triggerComp'").
const TYPE_META = {
  triggerComp: { label: "Trigger", color: "#d62976" },
  messageReply: { label: "Send Message", color: "#e6683c" },
  questionNode: { label: "Question", color: "#4f5bd5" },
  aiReplyNode: { label: "AI Reply", color: "#962fbf" },
  followersOnly: { label: "Followers Check", color: "#fa7e1e" },
  tagContact: { label: "Tag Contact", color: "#f59e0b" },
  triggerFlow: { label: "Trigger Flow", color: "#d62976" },
  delay: { label: "Delay", color: "#722ed1" },
  condition: { label: "Condition", color: "#722ed1" },
  httpRequest: { label: "HTTP Request", color: "#4f5bd5" },
  setField: { label: "Set Field", color: "#0ea5e9" },
  notes: { label: "Note", color: "#eab308" },
};

const CATEGORIES = ["Leads", "Sales", "AI", "Engagement", "Other"];

const truncate = (text, n) => {
  const s = String(text || "").replace(/\s+/g, " ");
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

// Icon tile per template (no emoji) — keyed by template id.
const TEMPLATE_ICONS = {
  "lead-collection": { icon: <SolutionOutlined />, color: "#4f5bd5" },
  "link-delivery": { icon: <LinkOutlined />, color: "#e6683c" },
  "email-collector-story": { icon: <MailOutlined />, color: "#d62976" },
  "ai-receptionist": { icon: <RobotOutlined />, color: "#962fbf" },
  "event-registration": { icon: <CalendarOutlined />, color: "#0ea5e9" },
  "price-inquiry": { icon: <DollarOutlined />, color: "#16a34a" },
  "giveaway-followers": { icon: <GiftOutlined />, color: "#fa7e1e" },
  "feedback-rating": { icon: <StarOutlined />, color: "#f59e0b" },
};

export const TemplateIcon = ({ template, size = 34 }) => {
  const meta = TEMPLATE_ICONS[template.id] || { icon: <AppstoreOutlined />, color: "#6366f1" };
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        flexShrink: 0,
        background: `${meta.color}1a`,
        color: meta.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.5),
      }}
    >
      {meta.icon}
    </span>
  );
};

/**
 * "New automation" popup: AI prompt box + the template library with live
 * graph previews. Click a template to preview it, then use it.
 */
export default function TemplateGalleryModal({ open, onClose, platform = "instagram" }) {
  const templates = templatesJson.templates || [];
  const [selectedId, setSelectedId] = useState(templates[0]?.id || null);
  const selected = templates.find((t) => t.id === selectedId) || templates[0];

  const openTemplate = (template) =>
    (window.location.href = `/flow-v2/editor?id=${uuidv4()}&template_id=${template.id}&platform=${platform}`);

  return (
    <Modal title="Create a new automation" open={open} footer={null} width={1020} onCancel={onClose}>
      {/* Top CTA row: describe it → AI builds it, or a blank canvas */}
      <div className="flex items-center" style={{ gap: 10, marginBottom: 16 }}>
        <AiFlowPromptBox platform={platform} style={{ flex: 1, minWidth: 0 }} />
        <Button
          size="large"
          icon={<EditOutlined />}
          onClick={() => (window.location.href = `/flow-v2/editor?id=${uuidv4()}&platform=${platform}`)}
          style={{ flexShrink: 0, borderRadius: 12, height: 48 }}
        >
          Start from scratch
        </Button>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--idm-text-faint)", marginBottom: 8 }}>
        Or start from a template
      </div>

      <div className="flex gap-4" style={{ minHeight: 430 }}>
        {/* Template list */}
        <div style={{ width: 300, flexShrink: 0, overflowY: "auto", maxHeight: 460, paddingRight: 4 }}>
          {CATEGORIES.map((cat) => {
            const items = templates.filter((t) => (t.category || "Other") === cat);
            if (!items.length) return null;
            return (
              <div key={cat} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--idm-text-faint)", margin: "4px 0 6px" }}>{cat}</div>
                {items.map((t) => {
                  const active = t.id === selectedId;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      className="cursor-pointer"
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        padding: "9px 10px",
                        marginBottom: 6,
                        borderRadius: 10,
                        border: `1.5px solid ${active ? "var(--idm-brand-500, #6366f1)" : "var(--idm-border)"}`,
                        background: active ? "var(--idm-brand-50, #eef2ff)" : "var(--idm-surface)",
                      }}
                    >
                      <TemplateIcon template={t} size={32} />
                      <div className="min-w-0">
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--idm-text)" }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: "var(--idm-text-muted)", lineHeight: 1.35 }}>{truncate(t.description, 60)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Preview pane */}
        {selected && (
          <div className="flex-1 min-w-0 flex flex-col" style={{ border: "1px solid var(--idm-border)", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ background: "var(--idm-bg)" }}>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center" style={{ height: 320 }}>
                    <Spin />
                  </div>
                }
              >
                <TemplatePreviewFlow template={selected} height={320} />
              </Suspense>
            </div>
            <div className="flex items-center gap-3" style={{ padding: "12px 16px", borderTop: "1px solid var(--idm-border)" }}>
              <TemplateIcon template={selected} size={40} />
              <div className="min-w-0" style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--idm-text)" }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: "var(--idm-text-muted)" }}>{selected.description}</div>
                <div className="flex gap-1 flex-wrap" style={{ marginTop: 6 }}>
                  {[...new Set(selected.nodes.map((n) => n.type))].map((type) => {
                    const meta = TYPE_META[type] || { label: type, color: "#94a3b8" };
                    return (
                      <Tag key={type} style={{ margin: 0, fontSize: 10, color: meta.color, borderColor: `${meta.color}59`, background: `${meta.color}14` }}>
                        {meta.label}
                      </Tag>
                    );
                  })}
                </div>
              </div>
              <Button type="primary" size="large" onClick={() => openTemplate(selected)}>
                Use this template
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
